import { isDefined } from "@clipboard-health/util-ts";
import { Box } from "@mui/material";
import { type ReactElement, useRef } from "react";
import { useController } from "react-hook-form";

interface Props {
  name: string;
  length?: number;
}

const OTP_LENGTH = 6;

export function OtpFieldBoxes(props: Props): ReactElement {
  const { name, length = OTP_LENGTH } = props;
  const {
    field,
    fieldState: { error },
    formState: { isSubmitting, isSubmitted },
  } = useController({
    name,
  });

  const inputRefs = useRef<Array<HTMLInputElement | undefined>>(
    Array.from({ length }, () => undefined)
  );

  const focusInput = (targetIndex: number) => {
    const targetInput = inputRefs.current[targetIndex];
    targetInput?.focus();
  };

  const selectInput = (targetIndex: number) => {
    const targetInput = inputRefs.current[targetIndex];
    targetInput?.select();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, currentIndex: number) => {
    const currentValue: string = (field.value as string | undefined) ?? "";

    switch (event.key) {
      case "ArrowUp":
      case "ArrowDown":
      case " ": {
        event.preventDefault();
        break;
      }

      case "ArrowLeft": {
        event.preventDefault();
        if (currentIndex > 0) {
          focusInput(currentIndex - 1);
          selectInput(currentIndex - 1);
        }

        break;
      }

      case "ArrowRight": {
        event.preventDefault();
        if (currentIndex < length - 1) {
          focusInput(currentIndex + 1);
          selectInput(currentIndex + 1);
        }

        break;
      }

      case "Delete": {
        event.preventDefault();
        const newValue = currentValue.slice(0, currentIndex) + currentValue.slice(currentIndex + 1);
        field.onChange(newValue);
        break;
      }

      case "Backspace": {
        event.preventDefault();

        // If current box has a value, delete it without moving focus
        if (currentValue[currentIndex]) {
          const newValue =
            currentValue.slice(0, currentIndex) + currentValue.slice(currentIndex + 1);
          field.onChange(newValue);
        }
        // If current box is empty, move to previous box and delete its value
        else if (currentIndex > 0) {
          const newValue =
            currentValue.slice(0, currentIndex - 1) + currentValue.slice(currentIndex);
          field.onChange(newValue);
          focusInput(currentIndex - 1);
          selectInput(currentIndex - 1);
        }

        break;
      }

      default: {
        // Only allow digits
        if (event.key && !/^\d$/.test(event.key) && !event.ctrlKey && !event.metaKey) {
          event.preventDefault();
        }

        break;
      }
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>, currentIndex: number) => {
    const currentValue = event.target.value;
    let indexToEnter = 0;

    while (indexToEnter <= currentIndex) {
      if (inputRefs.current[indexToEnter]?.value && indexToEnter < currentIndex) {
        indexToEnter += 1;
      } else {
        break;
      }
    }

    const previousValue: string = (field.value as string | undefined) ?? "";
    const otpArray = [...previousValue];
    const lastValue = currentValue.at(-1);

    if (lastValue && /^\d$/.test(lastValue)) {
      otpArray[indexToEnter] = lastValue;
    }

    const newValue = otpArray.join("").slice(0, length);
    field.onChange(newValue);

    if (currentValue !== "" && currentIndex < length - 1) {
      focusInput(currentIndex + 1);
    }
  };

  const handleClick = (_event: React.MouseEvent<HTMLInputElement>, currentIndex: number) => {
    selectInput(currentIndex);
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>, currentIndex: number) => {
    event.preventDefault();
    const { clipboardData } = event;

    if (clipboardData.types.includes("text/plain")) {
      const pastedTextRaw = clipboardData.getData("text/plain");
      const pastedText = pastedTextRaw.replace(/\D/g, "").slice(0, length).trim();
      let indexToEnter = 0;

      while (indexToEnter <= currentIndex) {
        if (inputRefs.current[indexToEnter]?.value && indexToEnter < currentIndex) {
          indexToEnter += 1;
        } else {
          break;
        }
      }

      const currentValue: string = (field.value as string | undefined) ?? "";
      const otpArray = [...currentValue];

      for (let index = indexToEnter; index < length; index += 1) {
        const lastValue = pastedText[index - indexToEnter] ?? " ";
        otpArray[index] = lastValue;
      }

      const newValue = otpArray.join("");
      field.onChange(newValue);

      // Focus the next empty box after pasting, unless we're at the last box
      const lastFilledIndex = indexToEnter + pastedText.length - 1;
      const nextEmptyIndex = lastFilledIndex + 1;

      if (nextEmptyIndex < length) {
        // Not at the last box, move to next empty box
        focusInput(nextEmptyIndex);
        selectInput(nextEmptyIndex);
      } else {
        // At or past the last box, stay on the last box
        focusInput(length - 1);
        selectInput(length - 1);
      }
    }
  };

  const value: string = (field.value as string | undefined) ?? "";
  const hasError = isDefined(error?.message) && isSubmitted;

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", justifyContent: "center" }}>
        {Array.from({ length }, (_, index) => (
          <Box
            key={`otp-input-${index}`}
            ref={(element: HTMLInputElement) => {
              inputRefs.current[index] = element;
            }}
            component="input"
            aria-label={`Digit ${index + 1} of OTP`}
            disabled={isSubmitting}
            inputMode="numeric"
            autoComplete="one-time-code"
            value={value[index] ?? ""}
            sx={(theme) => ({
              width: "48px",
              height: "64px",
              fontFamily: theme.typography.fontFamily,
              fontSize: 18,
              fontWeight: 500,
              lineHeight: 1.5,
              padding: 0,
              borderRadius: theme.shape.borderRadius,
              textAlign: "center",
              color: theme.palette.text.primary,
              background: "transparent",
              border: `1px solid ${
                hasError ? theme.palette.error.main : theme.palette.border?.primary
              }`,
              "&:hover": {
                borderColor: hasError ? theme.palette.error.main : theme.palette.primary.main,
              },
              "&:focus": {
                borderColor: hasError ? theme.palette.error.main : theme.palette.primary.main,
                borderWidth: "2px",
                outline: "none",
              },
              "&:disabled": {
                opacity: 0.5,
                cursor: "not-allowed",
              },
              "&:focus-visible": {
                outline: "none",
              },
            })}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              handleChange(event, index);
            }}
            onClick={(event: React.MouseEvent<HTMLInputElement>) => {
              handleClick(event, index);
            }}
            onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
              handleKeyDown(event, index);
            }}
            onPaste={(event: React.ClipboardEvent<HTMLInputElement>) => {
              handlePaste(event, index);
            }}
          />
        ))}
      </Box>
      {hasError && (
        <Box
          sx={(theme) => ({
            color: theme.palette.error.main,
            fontSize: "0.75rem",
            marginTop: 1,
            textAlign: "center",
          })}
        >
          {error.message}
        </Box>
      )}
    </Box>
  );
}
